import { useEffect, useState } from "react";
import { CalendarClock, Download, Loader2, Send, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/Layout";
import { Switch } from "@/components/ui/switch";
import { api, formatError, fmtDate } from "@/lib/api";

const saveBlob = (data, name) => {
  const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
  Object.assign(document.createElement("a"), { href: url, download: name }).click();
  URL.revokeObjectURL(url);
};

const ReportRow = ({ r, highlight }) => {
  const [busy, setBusy] = useState(false);
  const get = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/org/audit-reports/${r.id}/download`, { responseType: "blob" });
      saveBlob(data, `PostalCloud-audit-report-${r.period_from.slice(0, 7)}.csv`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li data-testid={`audit-report-${r.id}`} className={`flex flex-wrap items-center gap-3 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0 ${highlight ? "bg-emerald-50 dark:bg-emerald-900/20 -mx-3 px-3 rounded-lg" : ""}`}>
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm font-semibold">{r.period_from} to {r.period_to} <span className="ml-2 text-[11px] uppercase tracking-wider text-slate-500">{r.kind === "scheduled" ? "Monthly" : `Sent now by ${r.requested_by_name || "a team member"}`}</span></p>
        <p className="text-xs text-slate-500">{r.rows} entries · {r.flagged ? <span className="text-red-600 font-semibold">{r.flagged} flagged</span> : "no flags"} · emailed to {r.delivered}/{r.recipients} · {fmtDate(r.created_at)}</p>
        <p className="text-[11px] font-mono text-slate-400 break-all">SHA-256 {r.report_sha256}</p>
      </div>
      <button data-testid={`audit-report-download-${r.id}`} onClick={get} disabled={busy} className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-slate-300 dark:border-slate-700 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} CSV</button>
    </li>
  );
};

const Toggle = ({ id, label, hint, checked, disabled, onChange }) => (
  <label className="flex items-start gap-3 text-sm">
    <Switch data-testid={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
    <span><span className="font-semibold">{label}</span><span className="block text-xs text-slate-500">{hint}</span></span>
  </label>
);

export const AuditReports = ({ highlight }) => {
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get("/org/audit-reports").then((r) => setD(r.data)).catch((e) => toast.error(formatError(e)));
  }, []);
  if (!d) return null;
  const update = async (patch) => {
    try {
      const { data } = await api.patch("/org/audit-reports", { enabled: d.enabled, include_admins: d.include_admins, ...patch });
      setD(data);
      toast.success("Report settings saved");
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/org/audit-reports/send");
      setD({ ...d, reports: [data, ...d.reports] });
      toast.success(`Report emailed to ${data.delivered} of ${data.recipients} recipient(s)`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <Panel title="Scheduled audit reports" className="min-w-0" testId="audit-reports-panel">
      <div className="flex flex-wrap gap-6 items-start">
        <div className="space-y-4 flex-1 min-w-[260px]">
          <Toggle id="audit-reports-enabled-switch" label="Monthly report" hint="Emailed on the 1st of each month at 08:00 UTC, covering the previous month." checked={d.enabled} disabled={!d.can_edit} onChange={(v) => update({ enabled: v })} />
          <Toggle id="audit-reports-admins-switch" label="Also send to Admins" hint="By default only the Owner receives it." checked={d.include_admins} disabled={!d.can_edit} onChange={(v) => update({ include_admins: v })} />
          {!d.can_edit && <p className="text-xs text-slate-500" data-testid="audit-reports-owner-only">Only the Owner can change these settings.</p>}
        </div>
        <div className="space-y-2 text-sm">
          <p className="flex items-center gap-2 text-slate-600 dark:text-slate-300" data-testid="audit-reports-next-run"><CalendarClock size={15} className="text-emerald-600" /> Next report: {d.enabled ? fmtDate(d.next_run) : "paused"}</p>
          <button data-testid="audit-reports-send-now-btn" onClick={sendNow} disabled={sending} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-60">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send month-to-date now</button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-4 flex gap-2"><ShieldAlert size={14} className="shrink-0 text-emerald-600" /> Emails contain a summary and a sign-in link to the CSV, never an attachment. Every report sent and downloaded is recorded in the audit trail.</p>
      {d.reports.length > 0 && <ul className="mt-4" data-testid="audit-reports-list">{d.reports.map((r) => <ReportRow key={r.id} r={r} highlight={r.id === highlight} />)}</ul>}
    </Panel>
  );
};
